import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Grid,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import axios from "axios";

import { useNavigate } from "react-router-dom";

const API_URL = `${process.env.REACT_APP_API_URL}/api/tele/tech`;

const TechnicianDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [profile, setProfile] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [ticketStatusFilter, setTicketStatusFilter] = useState("Open");

  const [showProfile, setShowProfile] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editForm, setEditForm] = useState({
    primaryContact: "",
    oldPassword: "",
    newPassword: "",
  });

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  // ===== Fetch Profile =====
  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data.tech || res.data;
      setProfile(data);
      setEditForm((prev) => ({
        ...prev,
        primaryContact: data.primaryContact || "",
      }));
    } catch (err) {
      showSnackbar(
        err.response?.data?.msg || err.message || "Failed to load profile",
        "error"
      );
    }
  }, [token]);

  // ===== Fetch Tickets =====
  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data.tickets)
        ? res.data.tickets
        : res.data;
      setTickets(data || []);
    } catch (err) {
      showSnackbar(
        err.response?.data?.msg || err.message || "Failed to fetch tickets",
        "error"
      );
      setTickets([]);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
    fetchTickets();
  }, [fetchProfile, fetchTickets]);

  // ===== Debounced Search =====
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // ===== Filtered Tickets (Universal Search + Status Filter) =====
  const filteredTickets = tickets
    .filter((t) =>
      ticketStatusFilter === "All"
        ? true
        : t.ticketStatus?.toLowerCase() === ticketStatusFilter.toLowerCase()
    )
    .filter((t) => {
      const search = debouncedSearch.toLowerCase();
      return Object.values(t)
        .join(" ")
        .toLowerCase()
        .includes(search);
    });

  // ===== Profile Edit Handlers =====
  const handleProfileChange = (e) =>
    setEditForm({ ...editForm, [e.target.name]: e.target.value });

  const saveProfile = async () => {
    try {
      const payload = {};
      if (
        editForm.primaryContact &&
        editForm.primaryContact !== profile.primaryContact
      ) {
        payload.primaryContact = editForm.primaryContact;
      }
      if (editForm.oldPassword && editForm.newPassword) {
        payload.oldPassword = editForm.oldPassword;
        payload.newPassword = editForm.newPassword;
      }

      if (Object.keys(payload).length === 0)
        return showSnackbar("No changes made.", "info");

      const res = await axios.put(`${API_URL}/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showSnackbar(res.data.msg || "Profile updated successfully!");
      setOpenEditDialog(false);
      fetchProfile();
    } catch (err) {
      showSnackbar(
        err.response?.data?.msg || err.message || "Failed to update profile",
        "error"
      );
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Typography variant="h4" fontWeight="bold">
          Tele Technician Dashboard
        </Typography>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => {
            localStorage.removeItem("token");
            navigate("/login");
          }}
        >
          Logout
        </Button>
      </Box>

      {/* Profile Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">User Profile</Typography>
          <IconButton onClick={() => setShowProfile((prev) => !prev)}>
            {showProfile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={showProfile}>
          {!profile ? (
            <Typography>Loading profile...</Typography>
          ) : (
            <>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={4}>
                  <Typography>
                    <strong>Name:</strong> {profile.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography>
                    <strong>Primary Contact:</strong> {profile.primaryContact}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography>
                    <strong>Secondary Contact:</strong>{" "}
                    {profile.secondaryContact}
                  </Typography>
                </Grid>
              </Grid>
              <Box mt={2}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setOpenEditDialog(true)}
                >
                  Edit Profile
                </Button>
              </Box>
            </>
          )}
        </Collapse>
      </Paper>

      {/* Tickets Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Tickets
        </Typography>

        {/* Search + Status Filter */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          mb={2}
          flexWrap="wrap"
        >
          <TextField
            placeholder="🔍 Search tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={ticketStatusFilter}
              label="Status"
              onChange={(e) => setTicketStatusFilter(e.target.value)}
            >
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
              <MenuItem value="All">All</MenuItem>
            </Select>
          </FormControl>

          <IconButton color="primary" onClick={fetchTickets}>
            <RefreshIcon />
          </IconButton>
        </Stack>

        {/* Tickets Table */}
        {filteredTickets.length === 0 ? (
          <Typography>No tickets found.</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Branch Name</TableCell>
                  <TableCell>Pri.Contact</TableCell>
                  <TableCell>Sec.Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Ticket Status</TableCell>
                  <TableCell>CCTNumber</TableCell>
                  <TableCell>Company</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTickets.map((t, index) => (
                  <TableRow key={t._id ?? index} hover>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>{t.primaryContact}</TableCell>
                    <TableCell>{t.secondaryContact}</TableCell>
                    <TableCell>{t.status || "N/A"}</TableCell>
                    <TableCell>
                      {new Date(t.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>{t.ticketStatus || "N/A"}</TableCell>
                    <TableCell>{t.CCTNumber}</TableCell>
                    <TableCell>{t.company}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Edit Profile Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Change Primary Contact
          </Typography>
          <TextField
            label="Primary Contact"
            name="primaryContact"
            fullWidth
            margin="dense"
            size="small"
            value={editForm.primaryContact || ""}
            onChange={handleProfileChange}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Change Password
          </Typography>
          <TextField
            label="Old Password"
            type="password"
            name="oldPassword"
            fullWidth
            margin="dense"
            size="small"
            value={editForm.oldPassword || ""}
            onChange={handleProfileChange}
          />
          <TextField
            label="New Password"
            type="password"
            name="newPassword"
            fullWidth
            margin="dense"
            size="small"
            value={editForm.newPassword || ""}
            onChange={handleProfileChange}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setOpenEditDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={saveProfile}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TechnicianDashboard;
